import { useMemo } from 'react';
import { GrcTabPanel } from '@grc/ui-package';
import { useI18nState } from '../../../stores/settings/language-context';
import { quoteTabsConfig } from '../quote-management.config';
import { QuoteTabKey } from '../../../models/quotes/quote-tab.interface';
import { IDateRangeFilterValue } from '../../../common/date-range-filter/date-range-filter.interface';
import { ApprovalDecisionSource } from '../useApprovalDecision';
import QuoteGrid from '../grid/quote-grid';
import QuoteAudit from '../audit/quote-audit';
import './quote-tabs.scss';

interface IQuoteTabsProps {
  activeTab: QuoteTabKey;
  onTabChange: (tab: QuoteTabKey) => void;
  searchText: string;
  dateFilter: IDateRangeFilterValue;
  refreshToken: number;
  selectedCount: number;
  onApprove: (quoteIds: string[], source: ApprovalDecisionSource) => void;
  onReject: (quoteIds: string[], source: ApprovalDecisionSource) => void;
  onSelectionChange: (quoteIds: string[]) => void;
  onBulkApprove: () => void;
  onBulkReject: () => void;
}

interface IQuoteTabItem {
  id: QuoteTabKey;
  title: string;
}

export default function QuoteTabs({
  activeTab,
  onTabChange,
  searchText,
  dateFilter,
  refreshToken,
  selectedCount,
  onApprove,
  onReject,
  onSelectionChange,
  onBulkApprove,
  onBulkReject
}: IQuoteTabsProps) {
  const { localeData } = useI18nState();

  const dataSource: IQuoteTabItem[] = useMemo(
    () =>
      quoteTabsConfig.map(tab => ({
        id: tab.key,
        title: localeData[tab.labelKey] as string
      })),
    [localeData]
  );

  const selectedIndex = Math.max(
    dataSource.findIndex(item => item.id === activeTab),
    0
  );

  return (
    <GrcTabPanel
      className="grc-tabs quote-tabs"
      dataSource={dataSource}
      selectedIndex={selectedIndex}
      swipeEnabled={false}
      onSelectedIndexChange={(index: number) => {
        const nextKey = dataSource[index]?.id;
        if (nextKey && nextKey !== activeTab) {
          onTabChange(nextKey);
        }
      }}
      itemRender={(item: IQuoteTabItem) =>
        item.id === 'audit' ? (
          <QuoteAudit />
        ) : (
          <QuoteGrid
            tab={item.id}
            searchText={searchText}
            dateFilter={dateFilter}
            refreshToken={refreshToken}
            selectedCount={selectedCount}
            onApprove={onApprove}
            onReject={onReject}
            onSelectionChange={onSelectionChange}
            onBulkApprove={onBulkApprove}
            onBulkReject={onBulkReject}
          />
        )
      }
    />
  );
}
